import { gql } from "@apollo/client";

export const LOGIN = gql`
    mutation Login($email: String!, $password: String!) {
        login(email: $email, password: $password) {
            body
            status {
                statusCode
                authorized
                accessLevel
            }
        }
    }
`;

export const GET_INVOICES = gql`
    query GetInvoices(
        $filter: AndOrFilter
        $sortBy: String
        $sortOrder: Boolean
        $page: Int
        $pageSize: Int
    ) {
        getInvoices(
            filter: $filter
            sortBy: $sortBy
            sortOrder: $sortOrder
            page: $page
            pageSize: $pageSize
        ) {
            status {
                statusCode
                authorized
                accessLevel
            }
            body {
                invoiceID
                supplierEmail
                approverEmails
                originalAmount
                currency
                CADAmount
                GST
                PST
                details
                userNote
                date
                backOfficeNote
                rejectNote
                status
                paidDate
                receiptID
                expenseType
                category
                subCategory
                project
                distance
                distanceRateUsed
                distanceRateSource
                entity
                allocation
                capital
                fiscalYear
                GIFICode
                expenseGroup
                noApproverAvailable
                invoiceFilename
                reimbursed
            }
        }
    }
`;

export const GET_NOTIFICATIONS = gql`
    query GetNotifications($unreadOnly: Boolean) {
        getNotifications(unreadOnly: $unreadOnly) {
            status {
                statusCode
                authorized
                accessLevel
            }
            body {
                id
                recipientEmail
                recipientRole
                type
                message
                invoiceID
                isRead
                createdAt
            }
        }
    }
`;

export const GET_APPROVER_INVOICES = gql`
    query GetApproverInvoices(
        $filter: AndOrFilter
        $sortBy: String
        $sortOrder: Boolean
        $page: Int
        $pageSize: Int
    ) {
        getApproverInvoices(
            filter: $filter
            sortBy: $sortBy
            sortOrder: $sortOrder
            page: $page
            pageSize: $pageSize
        ) {
            status {
                statusCode
                authorized
                accessLevel
            }
            body {
                invoiceID
                supplierEmail
                approverEmails
                originalAmount
                currency
                CADAmount
                GST
                PST
                details
                userNote
                date
                backOfficeNote
                rejectNote
                status
                paidDate
                receiptID
                expenseType
                category
                subCategory
                project
                distance
                distanceRateUsed
                distanceRateSource
                entity
                allocation
                capital
                fiscalYear
                GIFICode
                expenseGroup
                noApproverAvailable
                invoiceFilename
                reimbursed
            }
        }
    }
`;

export const GENERATE_UPLOAD_URL = gql`
    mutation GenerateUploadUrl {
        generateUploadUrl {
            status {
                statusCode
                authorized
                accessLevel
            }
            body
        }
    }
`;

export const GET_ALL_TIERS = gql`
    query GetAllTiers {
        getAllTiers {
            status {
                statusCode
                authorized
                accessLevel
            }
            body {
                tierNumber
                threshold
            }
        }
    }
`;

export const GET_APPROVERS = gql`
    query GetApprovers(
        $filter: AndOrFilter
        $sortBy: String
        $sortOrder: String
        $page: Int
        $pageSize: Int
    ) {
        getApprovers(
            filter: $filter
            sortBy: $sortBy
            sortOrder: $sortOrder
            page: $page
            pageSize: $pageSize
        ) {
            status {
                statusCode
                authorized
                accessLevel
            }
            body {
                approverEmail
                tierNumber
                outOfOffice
                pendingReviewInvoices
            }
        }
    }
`;

export const CREATE_USER = gql`
    mutation CreateUser(
        $name: String!
        $email: String!
        $accessLevel: String!
        $organization: String!
        $tierNumber: Int
        $canSubmitDistanceInvoices: Boolean
        $customDistanceRate: Float
    ) {
        createUser(
            name: $name
            email: $email
            accessLevel: $accessLevel
            organization: $organization
            tierNumber: $tierNumber
            canSubmitDistanceInvoices: $canSubmitDistanceInvoices
            customDistanceRate: $customDistanceRate
        ) {
            status {
                statusCode
                authorized
                accessLevel
            }
        }
    }
`;
export const LOGOUT = gql`
    mutation Logout {
        logout {
            body
            status {
                statusCode
                authorized
                accessLevel
            }
        }
    }
`;

export const ADD_APPROVAL_TIER = gql`
    mutation AddApproverTier($threshold: Float!) {
        addApproverTier(threshold: $threshold) {
            body
            status {
                statusCode
                authorized
                accessLevel
            }
        }
    }
`;

export const DELETE_APPROVAL_TIER = gql`
    mutation DeleteApproverTier($tier: Int!) {
        deleteApproverTier(tier: $tier) {
            status {
                statusCode
                authorized
                accessLevel
            }
        }
    }
`;

export const CHANGE_APPROVAL_TIER_THRESHOLD = gql`
    mutation ChangeApproverTierThreshold(
        $tier: Int!
        $newThreshold: Float!
        $oldThreshold: Float!
    ) {
        changeApproverTierThreshold(
            tier: $tier
            newThreshold: $newThreshold
            oldThreshold: $oldThreshold
        ) {
            status {
                statusCode
                authorized
                accessLevel
            }
        }
    }
`;

export const GET_MY_PROFILE = gql`
    query GetUserProfile($email: String!) {
        getUserProfile(email: $email) {
            status {
                statusCode
                authorized
                accessLevel
            }
            body {
                email
                organization
                name
                phoneNumber
                addressLine1
                country
                postalCode
                city
                province
                accessLevel
                canSubmitDistanceInvoices
                customDistanceRate
                interacId
                securityAnswer
                voidChequeFilename
                voidChequeFileContent
                voidChequeFileSize
                voidChequeFileMimeType
                darkMode
            }
        }
    }
`;

export const GET_USER_PAYMENT_DETAILS = gql`
    query GetUserPaymentDetails($email: String!) {
        getUserProfile(email: $email) {
            status {
                statusCode
                authorized
                accessLevel
            }
            body {
                interacId
                securityAnswer
                voidChequeFilename
                voidChequeFileMimeType
                voidChequeFileContent
            }
        }
    }
`;

export const UPDATE_MY_PROFILE = gql`
    mutation ModifyUserProfile(
        $userEmail: String!
        $modification: UserProfileModificationBody!
    ) {
        modifyUserProfile(userEmail: $userEmail, modification: $modification) {
            body
            status {
                statusCode
                authorized
                accessLevel
            }
        }
    }
`;

export const UPLOAD_INVOICE = gql`
    mutation UploadInvoice(
        $CADAmount: Float!
        $originalAmount: Float!
        $currency: String!
        $gst: String
        $pst: String
        $details: String
        $expenseType: String
        $category: String
        $subCategory: String
        $project: String
        $distance: String
        $entity: String
        $allocation: String
        $userNote: String
        $invoiceFilename: String
        $invoiceFileContent: String
        $invoiceFileSize: Float
        $invoiceFileMimeType: String
        $date: String
        $distanceTrips: [InvoiceDistanceTripInput]
    ) {
        uploadInvoice(
            CADAmount: $CADAmount
            originalAmount: $originalAmount
            currency: $currency
            gst: $gst
            pst: $pst
            details: $details
            expenseType: $expenseType
            category: $category
            subCategory: $subCategory
            project: $project
            distance: $distance
            entity: $entity
            allocation: $allocation
            userNote: $userNote
            invoiceFilename: $invoiceFilename
            invoiceFileContent: $invoiceFileContent
            invoiceFileSize: $invoiceFileSize
            invoiceFileMimeType: $invoiceFileMimeType
            date: $date
            distanceTrips: $distanceTrips
        ) {
            body
            status {
                statusCode
                authorized
                accessLevel
            }
        }
    }
`;

export const GET_DASHBOARD = gql`
    query GetUserProfile($email: String!) {
        getUserProfile(email: $email) {
            body {
                dashboardCfg
            }
            status {
                statusCode
                authorized
                accessLevel
            }
        }
    }
`;

export const GET_INVOICE = gql`
    query GetInvoice($invoiceID: ID!) {
        getInvoice(invoiceID: $invoiceID) {
            body {
                invoiceID
                supplierEmail
                approverEmails
                originalAmount
                currency
                CADAmount
                GST
                PST
                details
                userNote
                date
                backOfficeNote
                rejectNote
                status
                receiptID
                expenseType
                category
                subCategory
                project
                distance
                distanceRateUsed
                distanceRateSource
                distanceTrips {
                    tripID
                    sortOrder
                    tripDate
                    fromLocation
                    toLocation
                    distance
                    notes
                }
                entity
                allocation
                capital
                fiscalYear
                GIFICode
                expenseGroup
                noApproverAvailable
                invoiceFilename
            }
            status {
                statusCode
                authorized
                accessLevel
            }
        }
    }
`;

export const GET_MY_SETTINGS = gql`
    query GetUserProfile($email: String!) {
        getUserProfile(email: $email) {
            body {
                darkMode
                tableDensity
                timeZone
            }
            status {
                statusCode
                authorized
                accessLevel
            }
        }
    }
`;

export const GET_USER_PROFILES = gql`
    query GetUserProfiles(
        $filter: AndOrFilter
        $sortBy: String
        $sortOrder: String
        $page: Int
        $pageSize: Int
    ) {
        getUserProfiles(
            filter: $filter
            sortBy: $sortBy
            sortOrder: $sortOrder
            page: $page
            pageSize: $pageSize
        ) {
            status {
                statusCode
                authorized
                accessLevel
            }
            body {
                email
                organization
                name
                accessLevel
                phoneNumber
                addressLine1
                country
                postalCode
                city
                province
                darkMode
                tableDensity
                timeZone
                dashboardCfg
            }
        }
    }
`;

export const APPROVE_INVOICE = gql`
    mutation ApproveInvoice($invoiceID: String!) {
        approveInvoice(invoiceID: $invoiceID) {
            body
            status {
                statusCode
                authorized
                accessLevel
            }
        }
    }
`;

export const DELETE_USER = gql`
    mutation DeleteUser($userEmail: String!) {
        deleteUser(userEmail: $userEmail) {
            body
            status {
                statusCode
                authorized
                accessLevel
            }
        }
    }
`;

export const GET_EXCHANGE_RATE = gql`
    query GetExchangeRate($currency: String!) {
        getExchangeRate(currency: $currency) {
            body
        }
    }
`;

export const GET_APPROVER_CREATION_REQUESTS_OF_APPROVER = gql`
    query GetApproverCreationRequestsOfApprover($email: String!) {
        getApproverCreationRequestsOfApprover(email: $email) {
            body {
                requestID
                approverEmail
                name
                organization
                submittedBy
                tierNumber
                threshold
                status
            }
            status {
                statusCode
                authorized
                accessLevel
            }
        }
    }
`;

export const REJECT_INVOICE = gql`
    mutation RejectInvoice($invoiceID: String!, $reason: String!, $editRequired: Boolean) {
        rejectInvoice(invoiceID: $invoiceID, reason: $reason, editRequired: $editRequired ) {
            body
            status {
                statusCode
                authorized
                accessLevel
            }
        }
    }
`;

export const ASSIGN_INVOICE_APPROVER = gql`
    mutation AssignInvoiceApprover($invoiceID: String!, $approverEmail: String!) {
        assignInvoiceApprover(invoiceID: $invoiceID, approverEmail: $approverEmail) {
            body
            status {
                statusCode
                authorized
                accessLevel
            }
        }
    }
`;

export const GET_OOF_MODE = gql`
    query GetApprover($email: String!) {
        getApprover(email: $email) {
            status {
                statusCode
                authorized
                accessLevel
            }
            body {
                outOfOffice
            }
        }
    }
`;

export const APPROVE_APPROVER_CREATION_REQUEST = gql`
    mutation ApproveApproverCreationRequest($requestID: String!) {
        approveApproverCreationRequest(requestID: $requestID) {
            status {
                statusCode
                authorized
                accessLevel
            }
        }
    }
`;

export const MODIFY_INVOICE = gql`
    mutation ModifyInvoice(
        $invoiceID: String!
        $originalAmount: String!
        $currency: String!
        $CADAmount: String
        $GST: String
        $PST: String
        $details: String
        $expenseType: String
        $category: String
        $entity: String
        $allocation: String
        $project: String
        $distance: String
        $GIFICode: String
        $fiscalYear: String
        $capital: String
        $subCategory: String
        $rejectNote: String
        $backOfficeNote: String
        $userNote: String
        $invoiceFilename: String
        $invoiceFileContent: String
        $invoiceFileSize: Float
        $invoiceFileMimeType: String
        $removeInvoiceFile: Boolean
        $reimbursed: Boolean
        $paidDate: String
        $clearPaidDate: Boolean
        $distanceTrips: [InvoiceDistanceTripInput]
        $oldValues: String!
    ) {
        modifyInvoice(
            invoiceID: $invoiceID
            originalAmount: $originalAmount
            currency: $currency
            CADAmount: $CADAmount
            GST: $GST
            PST: $PST
            details: $details
            expenseType: $expenseType
            category: $category
            entity: $entity
            allocation: $allocation
            project: $project
            distance: $distance
            GIFICode: $GIFICode
            fiscalYear: $fiscalYear
            capital: $capital
            subCategory: $subCategory
            rejectNote: $rejectNote
            backOfficeNote: $backOfficeNote
            userNote: $userNote
            invoiceFilename: $invoiceFilename
            invoiceFileContent: $invoiceFileContent
            invoiceFileSize: $invoiceFileSize
            invoiceFileMimeType: $invoiceFileMimeType
            removeInvoiceFile: $removeInvoiceFile
            reimbursed: $reimbursed
            paidDate: $paidDate
            clearPaidDate: $clearPaidDate
            distanceTrips: $distanceTrips
            oldValues: $oldValues
        ) {
            body
            status {
                statusCode
                authorized
                accessLevel
            }
        }
    }
`;

export const UPDATE_OOF_MODE = gql`
    mutation ChangeApproverOutOfOffice(
        $oldOutOfOffice: Boolean!
        $newOutOfOffice: Boolean!
    ) {
        changeApproverOutOfOffice(
            oldOutOfOffice: $oldOutOfOffice
            newOutOfOffice: $newOutOfOffice
        ) {
            body
            status {
                statusCode
                authorized
                accessLevel
            }
        }
    }
`;

export const MARK_NOTIFICATION_READ = gql`
    mutation MarkNotificationRead($id: Int!) {
        markNotificationRead(id: $id) {
            status {
                statusCode
                authorized
                accessLevel
            }
            body
        }
    }
`;

export const REJECT_APPROVER_CREATION_REQUEST = gql`
    mutation RejectApproverCreationRequest($requestID: String!) {
        rejectApproverCreationRequest(requestID: $requestID) {
            status {
                statusCode
                authorized
                accessLevel
            }
        }
    }
`;

export const CHANGE_APPROVER_TIER = gql`
    mutation ChangeApproverTier($userEmail: String!, $newTier: Int!) {
        changeApproverTier(userEmail: $userEmail, newTier: $newTier) {
            body
            status {
                statusCode
                authorized
                accessLevel
            }
        }
    }
`;


export const GET_TIER = gql`
    query GetApprover ($email: String!) {
        getApprover(email: $email) {
            status {
                statusCode
                authorized
                accessLevel
            }
            body {
                tierNumber
            }
        }
    }
`;

export const GET_TOTAL_REIMBURSED_AMOUNT = gql `
    query GetTotalReimbursedAmount($userEmail: String!) {
        getTotalReimbursedAmount(userEmail: $userEmail) {
            body
            status {
                statusCode
                authorized
                accessLevel
            }
        }
    }
`;

export const GET_TOTAL_UNPAID_AMOUNT = gql `
    query GetTotalUnpaidAmount($userEmail: String!, $filter: AndOrFilter ) {
        getTotalUnpaidAmount(userEmail: $userEmail, filter: $filter) {
            body
            status {
                statusCode
                authorized
                accessLevel
            }
        }
    }
`;

export const INVOICE_UPDATED_SUBSCRIPTION = gql`
    subscription InvoiceUpdated {
        invoiceUpdated {
            message
            senderEmail
        }
    }
`;

export const USER_UPDATED_SUBSCRIPTION = gql`
    subscription UserUpdated {
        userUpdated {
            message
            senderEmail
        }
    }
`;
